'use client'

import { useRef } from 'react'
import { useTranslations } from 'next-intl'

import { Icon, IconComponent } from '@graftcode/design-system'

import { GRAFTCODE_ONBOARDING_TEST_IDS } from '../../graftcode-onboarding-test-ids.const'
import { getGuideOsIcon } from '../../lib/getGuideOsIcon'
import {
  GRAFTCODE_ONBOARDING_GUIDE_OS_OPTIONS,
  type GraftcodeOnboardingGuideOs,
} from '../../lib/graftcode-onboarding-guide-os.const'

import styles from './GraftcodeOnboardingDashboard.module.scss'

type GraftcodeOnboardingOsPickerProps = {
  guideOs: GraftcodeOnboardingGuideOs
  onChange: (os: GraftcodeOnboardingGuideOs) => void
}

export function GraftcodeOnboardingOsPicker({ guideOs, onChange }: GraftcodeOnboardingOsPickerProps) {
  const t = useTranslations('graftcodeOnboarding')
  const detailsRef = useRef<HTMLDetailsElement>(null)

  const handleSelect = (option: GraftcodeOnboardingGuideOs) => {
    onChange(option)
    detailsRef.current?.removeAttribute('open')
  }

  return (
    <details ref={detailsRef} className={styles.osPicker} data-testid={GRAFTCODE_ONBOARDING_TEST_IDS.osPicker}>
      <summary className={styles.osPickerTrigger} aria-label={t('dashboard.osPickerLabel', { os: guideOs })}>
        <span className={styles.osPickerIcon} aria-hidden>
          <IconComponent icon={getGuideOsIcon(guideOs)} size={20} />
        </span>
        <span className={styles.osPickerLabel}>{guideOs}</span>
        <IconComponent icon={Icon.ChevronDownRegular} size={20} className={styles.osPickerChevron} />
      </summary>
      <div className={styles.osPickerMenu} role="group" aria-label={t('dashboard.osPickerMenuLabel')}>
        {GRAFTCODE_ONBOARDING_GUIDE_OS_OPTIONS.map((option) => {
          const active = option === guideOs
          return (
            <button
              key={option}
              type="button"
              className={styles.osPickerOption}
              aria-label={t('dashboard.osPickerOptionLabel', { os: option })}
              aria-pressed={active}
              onClick={() => handleSelect(option)}
            >
              <span className={styles.osPickerIcon} aria-hidden>
                <IconComponent icon={getGuideOsIcon(option)} size={20} />
              </span>
              <span className={styles.osPickerOptionLabel}>{option}</span>
              {active ? <IconComponent icon={Icon.CheckmarkRegular} size={16} /> : null}
            </button>
          )
        })}
      </div>
    </details>
  )
}
